import { useEffect, useRef } from 'react';

// Parallax depth factor per sticker (higher = moves more)
const PARALLAX = [0.18, 0.32, 0.24, 0.40, 0.14, 0.50, 0.22];

// Base rotation per sticker
const BASE_ROT = [-12, 15, -8, 20, -18, 10, -5];

const PxStar = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true">
    <g fill="currentColor">
      <rect x="14" y="2"  width="4" height="4"/>
      <rect x="10" y="6"  width="12" height="4"/>
      <rect x="2"  y="10" width="28" height="4"/>
      <rect x="6"  y="14" width="20" height="4"/>
      <rect x="10" y="18" width="12" height="4"/>
      <rect x="6"  y="22" width="4"  height="4"/>
      <rect x="22" y="22" width="4"  height="4"/>
      <rect x="2"  y="26" width="4"  height="4"/>
      <rect x="26" y="26" width="4"  height="4"/>
    </g>
  </svg>
);

const PxHeart = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true">
    <g fill="currentColor">
      <rect x="4"  y="6"  width="8"  height="4"/>
      <rect x="20" y="6"  width="8"  height="4"/>
      <rect x="2"  y="10" width="28" height="8"/>
      <rect x="6"  y="18" width="20" height="4"/>
      <rect x="10" y="22" width="12" height="4"/>
      <rect x="14" y="26" width="4"  height="4"/>
    </g>
  </svg>
);

const PxDiamond = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true">
    <g fill="currentColor">
      <rect x="14" y="4"  width="4"  height="4"/>
      <rect x="10" y="8"  width="12" height="4"/>
      <rect x="6"  y="12" width="20" height="4"/>
      <rect x="2"  y="16" width="28" height="4"/>
      <rect x="6"  y="20" width="20" height="4"/>
      <rect x="10" y="24" width="12" height="4"/>
      <rect x="14" y="28" width="4"  height="2"/>
    </g>
  </svg>
);

const PxFlower = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true">
    <g fill="currentColor">
      <rect x="14" y="2"  width="4"  height="4"/>
      <rect x="10" y="6"  width="12" height="4"/>
      <rect x="14" y="10" width="4"  height="4"/>
      <rect x="2"  y="14" width="12" height="4"/>
      <rect x="18" y="14" width="12" height="4"/>
      <rect x="14" y="18" width="4"  height="4"/>
      <rect x="10" y="22" width="12" height="4"/>
      <rect x="14" y="26" width="4"  height="4"/>
    </g>
  </svg>
);

const PxLightning = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true">
    <g fill="currentColor">
      <rect x="16" y="2"  width="8" height="4"/>
      <rect x="12" y="6"  width="8" height="4"/>
      <rect x="8"  y="10" width="8" height="4"/>
      <rect x="12" y="14" width="12" height="4"/>
      <rect x="8"  y="18" width="8" height="4"/>
      <rect x="4"  y="22" width="8" height="4"/>
      <rect x="8"  y="26" width="4" height="4"/>
    </g>
  </svg>
);

const PxSmile = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true">
    <g fill="currentColor">
      <rect x="8" y="4"  width="16" height="4"/>
      <rect x="4" y="8"  width="24" height="16"/>
      <rect x="8" y="24" width="16" height="4"/>
    </g>
    <g fill="var(--c-bg)">
      <rect x="10" y="12" width="4"  height="4"/>
      <rect x="18" y="12" width="4"  height="4"/>
      <rect x="10" y="18" width="12" height="2"/>
    </g>
  </svg>
);

const PxCd = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true">
    <g fill="currentColor">
      <rect x="10" y="4"  width="12" height="2"/>
      <rect x="6"  y="6"  width="20" height="2"/>
      <rect x="4"  y="8"  width="24" height="16"/>
      <rect x="6"  y="24" width="20" height="2"/>
      <rect x="10" y="26" width="12" height="2"/>
    </g>
    <g fill="var(--c-bg)">
      <rect x="12" y="14" width="8" height="4"/>
    </g>
  </svg>
);

export const Hero = ({ isVisible, onNavigate }) => {
  const stickersRef = useRef(null);

  useEffect(() => {
    const container = stickersRef.current;
    if (!container) return;

    const els = Array.from(container.querySelectorAll('.sticker'));
    let mx = 0, my = 0;
    let tmx = 0, tmy = 0;
    let rafId;
    const bobOffsets = els.map((_, i) => i * ((Math.PI * 2) / els.length));

    const onMove = (e) => {
      mx = (e.clientX / window.innerWidth  - 0.5) * 2;
      my = (e.clientY / window.innerHeight - 0.5) * 2;
    };

    const loop = (t) => {
      tmx += (mx - tmx) * 0.08;
      tmy += (my - tmy) * 0.08;

      els.forEach((el, i) => {
        const p   = PARALLAX[i] || 0.2;
        const rot = BASE_ROT[i] || 0;
        const bob = Math.sin(t * 0.001 + bobOffsets[i]) * 8;
        const dx  = tmx * 40 * p;
        const dy  = tmy * 40 * p + bob;
        el.style.transform = `translate(${dx}px, ${dy}px) rotate(${rot + tmx * 6 * p}deg)`;
      });

      rafId = requestAnimationFrame(loop);
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('pointermove', onMove);
    };
  }, []);

  return (
    <section id="home" className={`hero ${isVisible ? 'fade-in' : ''}`}>
      <div className="hero-bg" aria-hidden="true">
        <div className="grid-floor" />
        <div className="sun" />
      </div>

      <div className="hero-stickers" ref={stickersRef} aria-hidden="true">
        <div className="sticker s1"><PxStar /></div>
        <div className="sticker s2"><PxHeart /></div>
        <div className="sticker s3"><PxDiamond /></div>
        <div className="sticker s4"><PxFlower /></div>
        <div className="sticker s5"><PxSmile /></div>
        <div className="sticker s6"><PxLightning /></div>
        <div className="sticker s7"><PxCd /></div>
      </div>

      <div className="hero-inner">
        <div className="hero-badge">
          <span className="led" />
          <span>PORTFOLIO V26.04 · ONLINE</span>
        </div>

        <h1 className="hero-title">
          <span className="line"><span className="word">ALIF</span></span>
          <span className="line serif"><em>makes the</em></span>
          <span className="line"><span className="word alt">apps move</span></span>
        </h1>

        <p className="hero-sub">
          Mobile developer from Jakarta, shipping React&nbsp;Native apps
          for warungs, sales teams, and payment agents — five years,
          four production titles, zero chill.
        </p>

        <div className="hero-cta">
          <button className="btn-chunky" onClick={() => onNavigate('projects')}>
            ▶ PLAY DEMOS
          </button>
          <button className="btn-ghost" onClick={() => onNavigate('contact')}>
            ✉ SAY HI
          </button>
        </div>
      </div>

      <div className="hero-coin" aria-hidden="true">
        <div className="coin">
          <div className="face front">AG</div>
          <div className="face back">★</div>
        </div>
        <div className="coin-label">INSERT<br />COIN</div>
      </div>

      <div className="scroll-hint" aria-hidden="true">SCROLL ↓</div>
    </section>
  );
};
